import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { LoadingService } from 'src/app/services/loading-service.service';
import { LocaleService } from 'src/app/services/locale.service';
import { JobPostingsService } from 'src/app/services/openapi/api/api';
import {
  JobPostingResponse,
  JobPostingSearchCriteriaRequest,
  JobPostingsResponse,
  JobPostingsSearchOptionCountryResponse,
  JobPostingsSearchOptionResponse,
  JobPostingsSearchOptionSubdivisionResponse,
  JobPostingsSearchOptionsResponse,
} from 'src/app/services/openapi/model/models';
import { COUNTRY_SUBDIVISIONS, PCL_OPERATING_COUNTRIES, TRADE_SEARCH_HINTS } from 'src/app/shared/constants';
import { PostingComponent } from './posting/posting.component';

enum FormControlNames {
  PartialJobTitle = 'partialJobTitle',
  IsoCountry = 'isoCountry',
  IsoRegionalSubdivisions = 'isoRegionalSubdivisions',
  Cities = 'cities',
  JobSector = 'jobSector',
  CompanyName = 'companyName',
  SortBy = 'sortBy',
  OrderByAscending = 'orderByAscending',
  PageSize = 'pageSize',
  PageIndex = 'pageIndex',
}

enum SortOptions {
  Newest = 'newest',
  Oldest = 'oldest',
  Closing = 'closing',
}

interface SortOption {
  value: SortOptions;
  viewValue: string;
}

@Component({
  selector: 'app-postings',
  templateUrl: './postings.component.html',
  styleUrls: ['./postings.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    PostingComponent,
  ],
})
export class PostingsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  private readonly router: Router = inject(Router);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly localeService: LocaleService = inject(LocaleService);
  private readonly loadingService: LoadingService = inject(LoadingService);
  private readonly jobPostingsService: JobPostingsService = inject(JobPostingsService);
  private readonly queryParamMapping = {
    partialJobTitle: 'keyword',
    isoCountry: 'country',
    isoRegionalSubdivisions: 'subdivision',
    cities: 'city',
    jobSector: 'sector',
    companyName: 'company',
    sortBy: 'sortby',
    orderByAscending: 'orderbyascending',
  };
  private destroy$ = new Subject<void>();
  public readonly SortOptions = SortOptions;
  public readonly FormControlNames = FormControlNames;
  public readonly PCL_OPERATING_COUNTRIES = PCL_OPERATING_COUNTRIES;
  public readonly COUNTRY_SUBDIVISIONS = COUNTRY_SUBDIVISIONS;
  public readonly sortOptions: SortOption[] = [
    { value: SortOptions.Newest, viewValue: $localize`:@@SORT_OPTIONS.Newest:Newest` },
    { value: SortOptions.Oldest, viewValue: $localize`:@@SORT_OPTIONS.Oldest:Oldest` },
    { value: SortOptions.Closing, viewValue: $localize`:@@SORT_OPTIONS.Closing:Closing` },
  ];
  sortBy: SortOptions = this.sortOptions[0].value;
  postingSearchCriteriaForm: FormGroup;
  options: JobPostingsSearchOptionsResponse = {
    companies: [],
    sectors: [],
    countries: [],
  };
  sectors: JobPostingsSearchOptionResponse[] = [];
  companies: JobPostingsSearchOptionResponse[] = [];
  countries: JobPostingsSearchOptionCountryResponse[] = [];
  subdivisions: JobPostingsSearchOptionSubdivisionResponse[] = [];
  cities: JobPostingsSearchOptionResponse[] = [];
  searchHint = '';
  postings: JobPostingResponse[] = [];
  pageSizeOptions: number[] = [5, 10, 25, 100];
  totalPostings = 0;
  postingsLoaded = false;
  postingsLoadInProgress = false;
  filterChanges = new Subject<JobPostingSearchCriteriaRequest>();
  searchControl = new FormControl('');

  constructor() {
    this.postingSearchCriteriaForm = this.formBuilder.group({
      partialJobTitle: [''],
      isoCountry: [this.localeService.regionCode],
      isoRegionalSubdivisions: [[]],
      cities: [[]],
      jobSector: [null],
      companyName: [null],
      sortBy: ['PostingDate'],
      orderByAscending: [false],
      pageSize: [10],
      pageIndex: [0],
    });
  }

  ngOnInit(): void {
    this.getOptions().subscribe(() => {
      this.processQueryParams();
      this.onFilterChange();
      this.subscribeOnFormChanges();
      this.subscribeQueryParamsOnFormChanges();
    });

    // Subscribe to filter changes
    this.filterChanges.pipe(switchMap((criteria) => this.getPostings(criteria))).subscribe((postings) => {
      if (postings) {
        this.postings = postings.jobPostings;
        this.totalPostings = postings.totalCount as number;
      }
    });
  }

  private subscribeOnFormChanges(): void {
    this.postingSearchCriteriaForm.valueChanges.subscribe(() => {
      this.onFilterChange();
    });

    this.searchControl.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      this.postingSearchCriteriaForm.get(FormControlNames.PartialJobTitle)!.patchValue(value);
    });

    this.postingSearchCriteriaForm.get(FormControlNames.IsoCountry)!.valueChanges.subscribe((value) => {
      this.onCountrySelectionChange(value);
    });
    this.postingSearchCriteriaForm.get(FormControlNames.IsoRegionalSubdivisions)!.valueChanges.subscribe((value) => {
      this.onSubdivisionSelectionChange(value);
    });
  }

  /**
   * Subscribes to changes in the `postingSearchCriteriaForm` form group.
   * On each change, it maps the form values to a new object where the keys are the corresponding query parameters.
   * It only includes form controls that have a truthy value and a corresponding entry in the `queryParamMapping` object.
   * It then navigates to the current route with the new query parameters, replacing the current URL.
   */
  private subscribeQueryParamsOnFormChanges() {
    this.postingSearchCriteriaForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        map((values) => {
          return Object.entries(values).reduce((a, [k, v]) => {
            if (v && k in this.queryParamMapping) {
              const queryParam = this.queryParamMapping[k as keyof typeof this.queryParamMapping];
              return { ...a, [queryParam]: v };
            }
            return a;
          }, {});
        })
      )
      .subscribe((definedValues) => {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: definedValues,
          replaceUrl: true,
        });
      });
  }

  /** Processes the query parameters in the current route and map to form fields. */
  private processQueryParams(): void {
    const queryParamMap = this.route.snapshot.queryParamMap;

    if (queryParamMap.has('sector')) {
      const param = queryParamMap.get('sector');
      const sector = this.options.sectors.find((x) => x.name === param);
      if (sector) {
        this.postingSearchCriteriaForm.patchValue({ jobSector: sector.name }, { emitEvent: false });
      }
    }
    if (queryParamMap.has('company')) {
      const param = queryParamMap.get('company');
      const company = this.options.companies.find((x) => x.name === param);
      if (company) {
        this.postingSearchCriteriaForm.patchValue({ companyName: company.name }, { emitEvent: false });
      }
    }
    if (queryParamMap.has('country')) {
      const param = queryParamMap.get('country')!.toUpperCase();
      const country = this.options.countries.find((x) => x.name === param);
      if (country) {
        this.postingSearchCriteriaForm.patchValue({ isoCountry: country.name }, { emitEvent: false });
        this.onCountrySelectionChange(country.name);
      }
    }
    if (queryParamMap.has('subdivision')) {
      const params = queryParamMap.getAll('subdivision');
      const subdivisions = this.getAllSubdivisions().filter((x) => params.includes(x.name));
      if (subdivisions) {
        this.postingSearchCriteriaForm.patchValue(
          { isoRegionalSubdivisions: subdivisions.map((x) => x.name) },
          { emitEvent: false }
        );
        this.onSubdivisionSelectionChange(subdivisions.map((x) => x.name));
      }
    }
    if (queryParamMap.has('city')) {
      const params = queryParamMap.getAll('city');
      const cities = this.getAllCities().filter((x) => params.includes(x.name));
      if (cities) {
        this.postingSearchCriteriaForm.patchValue({ cities: cities.map((x) => x.name) }, { emitEvent: false });
      }
    }
    if (queryParamMap.has('keyword')) {
      const param = queryParamMap.get('keyword');
      this.postingSearchCriteriaForm.patchValue({ partialJobTitle: param }, { emitEvent: false });
      this.searchControl.setValue(param, { emitEvent: false });
    }
    if (queryParamMap.has('sortby')) {
      const sortByParam = queryParamMap.get('sortby');
      const orderByAscendingParam = queryParamMap.get('orderbyascending') === 'true';
      if (sortByParam === 'PostingDate' && !orderByAscendingParam) {
        this.sortBy = SortOptions.Newest;
      } else if (sortByParam === 'PostingDate' && orderByAscendingParam) {
        this.sortBy = SortOptions.Oldest;
      } else if (sortByParam === 'ExpiryDate') {
        this.sortBy = SortOptions.Closing;
      }
      this.postingSearchCriteriaForm.patchValue(
        { sortBy: sortByParam, orderByAscending: orderByAscendingParam },
        { emitEvent: false }
      );
    }
  }

  private getOptions(): Observable<JobPostingsSearchOptionsResponse> {
    this.loadingService.loadingOn();
    return this.jobPostingsService.getSearchOptions().pipe(
      tap((options) => {
        this.options = options;
        this.sectors = options.sectors;
        this.companies = options.companies.sort((a, b) => a.name.localeCompare(b.name));
        this.countries = options.countries.sort((a, b) => a.name.localeCompare(b.name));
        this.subdivisions = this.getAllSubdivisions();
        this.cities = this.getAllCities();

        this.onCountrySelectionChange(this.postingSearchCriteriaForm.get(FormControlNames.IsoCountry)!.value);
      }),
      finalize(() => {
        this.loadingService.loadingOff();
      }),
      catchError((error) => {
        console.error('Failed to get options:', error);
        const emptyOptions: JobPostingsSearchOptionsResponse = {
          companies: [],
          sectors: [],
          countries: [],
        };
        return of(emptyOptions);
      })
    );
  }

  private getAllSubdivisions(): JobPostingsSearchOptionSubdivisionResponse[] {
    return this.countries.flatMap((country) => country.subdivisions).sort((a, b) => a.name.localeCompare(b.name));
  }

  private getAllCities(): JobPostingsSearchOptionResponse[] {
    return this.countries
      .flatMap((country) => country.subdivisions)
      .flatMap((subdivision) => subdivision.cities)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private getPostings(criteria: JobPostingSearchCriteriaRequest): Observable<JobPostingsResponse> {
    this.loadingService.loadingOn();
    return this.jobPostingsService.getJobPostings(criteria).pipe(
      finalize(() => {
        this.loadingService.loadingOff();
        this.postingsLoaded = true;
      }),
      catchError((error) => {
        console.error(error);
        const emptyPostings: JobPostingsResponse = {
          jobPostings: [],
          totalCount: 0,
        };
        return of(emptyPostings);
      })
    );
  }

  private onFilterChange(): void {
    const formValues = this.postingSearchCriteriaForm.value;
    const definedValues = Object.entries(formValues).reduce(
      (a, [k, v]) => (v != null && !(Array.isArray(v) && v.length === 0) ? { ...a, [k]: v } : a),
      {}
    );
    const postingSearchCriteria = Object.assign({}, definedValues);

    this.filterChanges.next(postingSearchCriteria);
  }

  private onCountrySelectionChange(selectedCountry: string): void {
    // Limit province / state selection to the selected country
    this.subdivisions =
      this.countries.find((country) => country.name === selectedCountry)?.subdivisions ?? this.getAllSubdivisions();

    // Patch out any provinces / states that are no longer in the list of subdivisions
    const currentSelectedSubdivisions =
      this.postingSearchCriteriaForm.get(FormControlNames.IsoRegionalSubdivisions)!.value || [];
    const intersection = this.subdivisions.filter((subdivision) =>
      currentSelectedSubdivisions.includes(subdivision.name)
    );
    this.postingSearchCriteriaForm.patchValue(
      { isoRegionalSubdivisions: intersection.map((x) => x.name) },
      { emitEvent: false }
    );

    // Clean province / state selection
    if (intersection.length === 0) {
      this.onSubdivisionSelectionChange(this.subdivisions.map((x) => x.name));
    } else {
      this.onSubdivisionSelectionChange(intersection.map((x) => x.name));
    }
  }

  private onSubdivisionSelectionChange(selectedSubdivisions: string[]): void {
    this.cities = this.subdivisions
      .filter((subdivision) => selectedSubdivisions.includes(subdivision.name))
      .flatMap((subdivision) => subdivision.cities);

    // Patch out any cities that are no longer in the list of cities
    const currentSelectedCities = this.postingSearchCriteriaForm.get(FormControlNames.Cities)!.value || [];
    const intersection = this.cities.filter((city) => currentSelectedCities.includes(city.name));
    this.postingSearchCriteriaForm.patchValue({ cities: intersection.map((x) => x.name) }, { emitEvent: false });

    if (this.cities.length === 0) {
      this.cities = this.getAllCities();
    }
  }

  public onPageChange(event: PageEvent) {
    this.postingSearchCriteriaForm.patchValue({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  public sortPostings(sortBy: SortOptions): void {
    switch (sortBy) {
      default:
      case SortOptions.Newest:
        this.postingSearchCriteriaForm.patchValue({ sortBy: 'PostingDate', orderByAscending: false });
        break;
      case SortOptions.Oldest:
        this.postingSearchCriteriaForm.patchValue({ sortBy: 'PostingDate', orderByAscending: true });
        break;
      case SortOptions.Closing:
        this.postingSearchCriteriaForm.patchValue({ sortBy: 'ExpiryDate', orderByAscending: true });
        break;
    }
  }

  /** Used to generate a random placeholder for the "Search by keyword" input. */
  public onSearchFocus(): void {
    let randomIndex: number;
    do {
      randomIndex = Math.floor(Math.random() * TRADE_SEARCH_HINTS.length);
    } while (TRADE_SEARCH_HINTS[randomIndex] === this.searchHint);
    this.searchHint = TRADE_SEARCH_HINTS[randomIndex];
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
}
